import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export type Store = StoreFromGet | StoreFromGetMany;

type StoreFromGet = Endpoints['GET /stores/:id']['response'];

export const useStore = (id?: string | null) => {
  const fetch = useFetch<Endpoints['GET /stores/:id']>();

  return useQuery({
    queryKey: ['stores', id],
    queryFn: () => fetch(`/stores/${id!}`),
    enabled: !!id,
  });
};

export type StoreFromGetMany = Endpoints['GET /stores']['response']['stores'][number];
export const useStores = (
  params: Endpoints['GET /stores']['query'],
  options?: {
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /stores']>();

  return useQuery({
    queryKey: ['stores', params],
    queryFn: () => fetch('/stores', params),
    enabled: options?.enabled,
    placeholderData: params?.search ? keepPreviousData : undefined,
  });
};

export const useInfiniteStores = (
  params: Endpoints['GET /stores']['query'],
  options?: {
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /stores']>();
  const limit = params.limit ?? 20;

  return useInfiniteQuery<
    Endpoints['GET /stores']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /stores']['response']>,
    QueryKey,
    number
  >({
    queryFn: ({ pageParam }) =>
      fetch('/stores', {
        ...params,
        offset: pageParam,
      }),
    queryKey: ['stores', params],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.stores.length < limit) {
        return undefined;
      } else {
        return lastPage.meta.offset + limit;
      }
    },
    enabled: options?.enabled,
  });
};

export const useCreateStore = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /stores']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /stores']['body']) =>
      fetch('/stores', undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });
};

export const useUpdateStore = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /stores/:id']>();

  return useMutation({
    mutationFn: ({
      storeId,
      ...body
    }: {
      storeId: string;
    } & Endpoints['PATCH /stores/:id']['body']) =>
      fetch(`/stores/${storeId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });
};

export const useUpdateExternalStore = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /stores/external/:id']>();

  return useMutation({
    mutationFn: ({
      storeId,
      ...body
    }: {
      storeId: string;
    } & Endpoints['PATCH /stores/external/:id']['body']) =>
      fetch(`/stores/external/${storeId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
};

export const useDeleteStore = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /stores/:id']>();

  return useMutation({
    mutationFn: (storeId: string) => fetch(`/stores/${storeId}`, undefined, { method: 'DELETE' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
    },
  });
};
